import { permissionEngine } from '@/core/permissions/permissionEngine';
import { useAuth } from '@/core/auth/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { useState } from 'react';
import { useInventoryDetail } from './hooks/useInventoryDetail';
import { useCurrentUser } from '@/shared/hooks/useCurrentUser';

// Modular Components
import { InventoryDetailHeader } from './components/InventoryDetailHeader';
import { InventoryDetailInfo } from './components/InventoryDetailInfo';
import { InventoryDetailTabs } from './components/InventoryDetailTabs';
import { InventoryDetailSpecs } from './components/InventoryDetailSpecs';
import { InventoryDetailStockBreakdown } from './components/InventoryDetailStockBreakdown';
import { InventoryDetailSideCards } from './components/InventoryDetailSideCards';

export function InventoryDetailPage() {
  const { profile } = useAuth();
  permissionEngine.assert(profile?.id || '', 'inventory.items.view');

 const { id = '' } = useParams();
 const navigate = useNavigate();
 const { user: currentUser } = useCurrentUser();
 const { item, isLoading, isError, error, refetch, canUpdate, warehouseStock } = useInventoryDetail(id);
 const [activeTab, setActiveTab] = useState('detail');

 if (isLoading) {
 return (
 <div className="flex flex-col gap-4 pb-8 animate-pulse">
 <div className="h-64 rounded-md bg-white p-3" />
 <div className="h-10 rounded-md bg-white p-3" />
 <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
 <div className="h-96 rounded-md bg-white" />
 <div className="h-96 rounded-md bg-white" />
 <div className="h-96 rounded-md bg-white" />
 </div>
 </div>
 );
 }

 if (isError || !item) {
 return (
 <div className="flex flex-col items-center justify-center rounded-md border border-slate-200 bg-white p-12 text-center">
 <AlertCircle className="h-10 w-10 text-rose-500 mb-3" />
 <p className="sm:text-sm text-xs text-slate-900">{(error as Error)?.message || 'Không tìm thấy thông tin vật tư.'}</p>
 <button type="button" onClick={() => navigate('/inventory/items')} className="mt-4 rounded-md bg-emerald-700 px-6 py-2 sm:text-sm text-xs text-white hover:bg-emerald-800">
 Quay lại danh sách
 </button>
 </div>
 );
 }

 const stockValue = item.current_stock * item.unit_price;

 return (
 <div className="flex flex-col gap-4 pb-8">
 {/* 1. Header Section */}
 <InventoryDetailHeader 
 item={item} 
 canUpdate={canUpdate} 
 />

 {/* 2. Main Info Block */}
 <InventoryDetailInfo 
 item={item} 
 stockValue={stockValue} 
 />

 {/* 3. Tabs Navigation */}
 <InventoryDetailTabs 
 activeTab={activeTab} 
 setActiveTab={setActiveTab} 
 />

 {/* 4. Tab Content: detail info with 3 blocks */}
 <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
 {/* Block 1: Technical Specs */}
 <InventoryDetailSpecs 
 item={item} 
 canUpdate={canUpdate}
 companyId={currentUser?.companyId || profile?.company_id || ''}
 userId={currentUser?.id || profile?.id || ''}
 onRefresh={refetch}
 />

 {/* Block 2: Stock by Warehouse */}
 <InventoryDetailStockBreakdown 
 item={item} 
 warehouseStock={warehouseStock}
 canUpdate={canUpdate}
 />

 {/* Block 3: Supplier & Notes */}
 <InventoryDetailSideCards 
 item={item} 
 canUpdate={canUpdate}
 />
 </div>

 {/* Optional: Tab specific content area below if needed */}
 {activeTab !== 'detail' && (
 <div className="rounded-md border border-slate-200 bg-white p-12 text-center">
 <p className="sm:text-sm text-xs text-slate-400 italic">Nội dung bổ sung cho tab "{activeTab}" đang được đồng bộ...</p>
 </div>
 )}
 </div>
 );
}

export default InventoryDetailPage;




